import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ShoppingCart, ArrowLeft, Check, Sparkles, Droplets, Flame } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Guest cart helpers
const getGuestCart = () => JSON.parse(localStorage.getItem('guestCart') || '[]');
const saveGuestCart = (cart) => localStorage.setItem('guestCart', JSON.stringify(cart));

export default function ProductDetailPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user, fetchCartCount } = useContext(AuthContext);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${productId}`);
        setProduct(response.data);
      } catch (error) {
        console.error('Error fetching product:', error);
        toast.error('Product not found');
        navigate('/products');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId, navigate]);

  const addToCart = () => {
    if (user) {
      axios.post(`${API}/cart/add`, { item_id: product.id, item_type: 'product', quantity: 1 }, { withCredentials: true })
        .then(() => {
          fetchCartCount();
          toast.success('Added to cart!');
        })
        .catch(() => toast.error('Failed to add to cart'));
    } else {
      // Guest user
      const guestCart = getGuestCart();
      const existingItem = guestCart.find(item => item.cart_item.item_id === product.id);
      
      if (existingItem) {
        existingItem.cart_item.quantity += 1;
      } else {
        guestCart.push({
          cart_item: {
            id: `guest_${Date.now()}`,
            item_id: product.id,
            item_type: 'product',
            quantity: 1
          },
          item_details: {
            id: product.id,
            name: product.name,
            price: product.price,
            image_url: product.image_url,
            type: product.type
          }
        });
      }
      
      saveGuestCart(guestCart);
      toast.success('Added to cart!');
      window.dispatchEvent(new Event('guestCartUpdated'));
    }
  };

  // Get product type icon and color
  const getProductTypeInfo = (type) => {
    const types = {
      essential_oil: { icon: Droplets, color: 'bg-purple-100 text-purple-700', label: 'Essential Oil' },
      candle: { icon: Flame, color: 'bg-amber-100 text-amber-700', label: 'Candle' },
      bar_soap: { icon: Sparkles, color: 'bg-pink-100 text-pink-700', label: 'Bar Soap' },
      bath_bomb: { icon: Sparkles, color: 'bg-pink-100 text-pink-700', label: 'Bath Bomb' },
      body_cream: { icon: Sparkles, color: 'bg-pink-100 text-pink-700', label: 'Body Cream' },
      sugar_scrub: { icon: Sparkles, color: 'bg-pink-100 text-pink-700', label: 'Sugar Scrub' },
      whipped_butter: { icon: Sparkles, color: 'bg-pink-100 text-pink-700', label: 'Whipped Butter' },
      whipped_soap: { icon: Sparkles, color: 'bg-pink-100 text-pink-700', label: 'Whipped Soap' },
    };
    return types[type] || { icon: Sparkles, color: 'bg-gray-100 text-gray-700', label: 'Accessory' };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (!product) return null;

  const typeInfo = getProductTypeInfo(product.type);
  const TypeIcon = typeInfo.icon;

  return (
    <div 
      className="min-h-screen" 
      style={{ 
        backgroundImage: 'linear-gradient(rgba(204, 85, 0, 0.85), rgba(204, 85, 0, 0.85)), url(https://images.pexels.com/photos/12336389/pexels-photo-12336389.jpeg?auto=compress&cs=tinysrgb&w=1920)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)} 
          className="mb-6 text-white hover:bg-white/10"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Product Image */}
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <img 
              src={product.image_url} 
              alt={product.name} 
              className="w-full h-full object-cover"
            />
          </div>

          {/* Product Details */}
          <div className="space-y-6 bg-white/95 backdrop-blur-sm p-8 rounded-2xl">
            <div>
              <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm mb-4 ${typeInfo.color}`}>
                <TypeIcon className="w-4 h-4" />
                {typeInfo.label}
              </span>
              <h1 
                className="text-4xl font-bold text-gray-900 mb-4" 
                style={{ fontFamily: 'Playfair Display, serif' }}
                data-testid="product-name"
              >
                {product.name}
              </h1>
              <p className="text-2xl font-bold text-orange-600 mb-6" data-testid="product-price">
                ${product.price.toFixed(2)}
              </p>
            </div>

            {/* Scent */}
            {product.scent && (
              <div className="flex items-center gap-2 text-purple-700 bg-purple-50 px-4 py-2 rounded-lg">
                <span className="text-lg">🌸</span>
                <span className="font-medium">Scent: {product.scent}</span>
              </div>
            )}

            {/* Description */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                Description
              </h2>
              <p className="text-gray-700 leading-relaxed">{product.description}</p>
            </div>

            {/* Benefits Section */}
            {product.benefits && product.benefits.length > 0 && (
              <div className="bg-emerald-50 p-4 rounded-xl">
                <h3 className="text-lg font-semibold text-emerald-800 mb-3 flex items-center">
                  <Sparkles className="w-5 h-5 mr-2" />
                  Key Benefits
                </h3>
                <ul className="space-y-2">
                  {product.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-emerald-700">
                      <Check className="w-4 h-4 mt-1 flex-shrink-0" />
                      <span className="text-sm">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Category */}
            {product.category && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Category</h3>
                <span className="inline-block bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                  {product.category}
                </span>
              </div>
            )}

            {/* Add to Cart Button */}
            <Button
              size="lg"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-lg py-6 rounded-full"
              onClick={addToCart}
              data-testid="add-to-cart-button"
            >
              <ShoppingCart className="mr-2 w-5 h-5" />
              Add to Cart
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
